import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ContentHeaderComponent } from '@app/components/content-header/content-header.component';
import { NavigationComponent } from '@app/components/navigation/navigation.component';
import { PAGE_MAP, PAGE_ROUTE } from '@app/constants/page-route';
import { IsGrantedDirective } from '@app/directives/is-granted.directive';
import { QrCodeListStepperComponent } from '@app/modules/client-management/components/qr-onboarding/qr-code-list-stepper/qr-code-list-stepper.component';
import { QRListStepperStageType } from '@app/modules/client-management/enums/qr-list-stepper-stage-type.enum';

import {
    AccessRoleType,
    SupplyChainAccessRoleType,
} from '@app/modules/user-management/enums/access-role-type.enum';
import { DataTestIdPipe } from '@app/pipes/data-test-id/data-test-id-pipe';
import { MsalAuthService } from '@app/services/msal-auth/msal-auth.service';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { AppStore } from '@app/stores/app/app.store';
import { CompanyStore } from '@app/stores/company/company.store';
import { environment } from '@env/environment';
import { InterpolateComponent } from '@my-ul/tod-angular-client';
import { SpinnerLoaderComponent } from '../../components/spinner-loader/spinner-loader.component';
import { ServiceLineType } from '@app/enums/service-type.enum';

@Component({
    selector: 'app-client-management-shell',
    imports: [
        RouterModule,
        MatIconModule,
        MatChipsModule,
        NavigationComponent,
        ContentHeaderComponent,
        MatButtonModule,
        DataTestIdPipe,
        InterpolateComponent,
        IsGrantedDirective,
        SpinnerLoaderComponent,
    ],
    templateUrl: './client-management-shell.component.html',
    styleUrls: ['./client-management-shell.component.scss'],
})
export class ClientManagementShellComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private navigationService = inject(NavigationService);
    private msalAuthService = inject(MsalAuthService);
    private _bottomSheet = inject(MatBottomSheet);
    isSupplyChainUser = this.msalAuthService.hasSupplyChainAccessRoles();
    private appStore = inject(AppStore);
    translate = this.appStore.i18n;
    private companyStore = inject(CompanyStore);
    clientInfo = this.companyStore.clientDetailInfo;
    readonly companyId = String(this.route.snapshot.paramMap.get('id'));
    navs = computed(() => {
        const { Users, TITLE__VendorSupplier } = this.translate();

        return [
            {
                navId: 1,
                displayText: Users,
                description: '',
                targetUrl: `/${PAGE_ROUTE.Main}/${PAGE_ROUTE.ClientManagement}/${this.companyId}/${PAGE_ROUTE.ClientUsers}`,
                icon: '',
                sortOrder: 1,
                roles: [
                    {
                        role: environment.accessRoles[AccessRoleType.AccountManager],
                        scRole: '',
                        serviceLines: [
                            environment.serviceLines[ServiceLineType.Testing],
                            environment.serviceLines[ServiceLineType.Inspection],
                        ],
                    },
                    {
                        role: environment.accessRoles[AccessRoleType.CompanyAdmin],
                        scRole: environment.supplyChainAccessRoles[
                            SupplyChainAccessRoleType.Client
                        ],
                        serviceLines: [
                            environment.serviceLines[ServiceLineType.Testing],
                            environment.serviceLines[ServiceLineType.Inspection],
                        ],
                    },
                    {
                        role: environment.accessRoles[AccessRoleType.CompanyPrimaryAdmin],
                        scRole: environment.supplyChainAccessRoles[
                            SupplyChainAccessRoleType.Client
                        ],
                        serviceLines: [
                            environment.serviceLines[ServiceLineType.Testing],
                            environment.serviceLines[ServiceLineType.Inspection],
                        ],
                    },
                    {
                        role: environment.accessRoles[AccessRoleType.CompanyAdmin],
                        scRole: environment.supplyChainAccessRoles[
                            SupplyChainAccessRoleType.Vendor
                        ],
                        serviceLines: [
                            environment.serviceLines[ServiceLineType.Testing],
                            environment.serviceLines[ServiceLineType.Inspection],
                        ],
                    },
                    {
                        role: environment.accessRoles[AccessRoleType.CompanyPrimaryAdmin],
                        scRole: environment.supplyChainAccessRoles[
                            SupplyChainAccessRoleType.Vendor
                        ],
                        serviceLines: [
                            environment.serviceLines[ServiceLineType.Testing],
                            environment.serviceLines[ServiceLineType.Inspection],
                        ],
                    },
                ],
            },
            {
                navId: 2,
                displayText: TITLE__VendorSupplier,
                description: '',
                targetUrl: `/${PAGE_ROUTE.Main}/${PAGE_ROUTE.ClientManagement}/${this.companyId}/${PAGE_ROUTE.VendorSupplier}`,
                icon: '',
                sortOrder: 2,
                roles: [
                    {
                        role: environment.accessRoles[AccessRoleType.AccountManager],
                        scRole: '',
                        serviceLines: [
                            environment.serviceLines[ServiceLineType.Testing],
                            environment.serviceLines[ServiceLineType.Inspection],
                        ],
                    },
                    {
                        role: environment.accessRoles[AccessRoleType.CompanyAdmin],
                        scRole: environment.supplyChainAccessRoles[
                            SupplyChainAccessRoleType.Client
                        ],
                        serviceLines: [environment.serviceLines[ServiceLineType.Testing]],
                    },
                    {
                        role: environment.accessRoles[AccessRoleType.CompanyPrimaryAdmin],
                        scRole: environment.supplyChainAccessRoles[
                            SupplyChainAccessRoleType.Client
                        ],
                        serviceLines: [environment.serviceLines[ServiceLineType.Testing]],
                    },
                ],
            },
        ];
    });

    private state = signal({
        showAvatarInitials: false,
        companyImageUrl: '',
    });
    companyImageUrl = computed(() => this.state().companyImageUrl);
    showAvatarInitials = computed(() => this.state().showAvatarInitials);

    viewActiveQrRoles = computed(() => {
        return [
            {
                role: environment.accessRoles[AccessRoleType.AccountManager],
                scRole: '',
                serviceLines: [],
            },
        ];
    });

    isBusy = computed(() => this.companyStore.isBusy());

    companyInitials = computed(() => {
        const name = this.companyName();
        const words = name.split(' ');
        return words.length > 1
            ? words[0].charAt(0) + words[1].charAt(0)
            : words[0].charAt(0);
    });

    companyName = computed(() => this.clientInfo().company.name);
    companyAddress = computed(() => this.clientInfo().company.address);
    userName = computed(() => this.clientInfo().primaryUser.userName);
    userRole = computed(() => this.clientInfo().primaryUser.userRole);
    phoneNumber = computed(() => this.clientInfo().primaryUser.userPhoneNumber);
    email = computed(() => this.clientInfo().primaryUser.userEmail);
    activeQrCodes = computed(() => this.clientInfo().activeQRCodes.numberOfActiveQRCodes);
    activeQRCodeLimit = computed(() => this.clientInfo().activeQRCodes.maxActiveQRCodes);

    companyInfo = computed(() => [
        {
            id: 1,
            testId: 'company-details-mobile',
            value: this.companyName(),
            icon: 'mobile_friendly',
        },
        {
            id: 2,
            testId: 'company-details-address',
            value: this.clientInfo().company.address,
            icon: 'pin_drop',
        },
    ]);
    primaryAdminInfo = computed(() => [
        {
            id: 1,
            testId: 'company-details-username-email',
            value: this.clientInfo().primaryUser.userEmail,
            icon: 'mail_outline',
        },
    ]);

    ngOnInit() {
        this.appStore.setTitlePage({ value: 'Manage Roles & Access' });
        this.initClientDetailInfo();
    }

    initClientDetailInfo() {
        const defaultSupplyChainRole = this.msalAuthService.getDefaultSupplyChainRole();
        const isVendorSuppler =
            defaultSupplyChainRole ===
            environment.supplyChainAccessRoles[SupplyChainAccessRoleType.Vendor];
        this.appStore.setIsVendorUser({ value: isVendorSuppler });
        if (isVendorSuppler) {
            this.companyStore.getVendorDetailInfo({ id: this.companyId });
        } else {
            this.companyStore.getClientDetailInfo({ id: this.companyId });
        }
    }

    onBack(e: Event) {
        e.preventDefault();
        if (this.isSupplyChainUser) {
            const appClaims = this.msalAuthService.getAppClaims();
            const defaultUrl = this.navigationService.getDefaultUrl(appClaims);
            if (defaultUrl.includes(PAGE_MAP.main.inspection.default)) {
                const prevUrl = this.appStore.prevInspectionPage();
                if (
                    prevUrl ===
                    `${PAGE_ROUTE.Main}/${PAGE_ROUTE.Inspection}/${PAGE_ROUTE.InspectionAnalytics}`
                ) {
                    this.navigationService.goToInspectionAnalyticsPage();
                    return;
                }
                this.navigationService.goToInspectionPage();
            } else {
                this.navigationService.goToETRFPage();
            }
            return;
        }
        this.navigationService.goToUserManagementClientPage();
    }

    onImageError() {
        this.state.update((state) => ({ ...state, showAvatarInitials: true }));
    }

    onViewActiveQrCodes(e: Event) {
        e.preventDefault();
        const bottomSheet = this._bottomSheet.open(QrCodeListStepperComponent, {
            panelClass: 'rcp-client-bottom-sheet',
            closeOnNavigation: true,
            data: { id: this.companyId },
        });
        void bottomSheet.afterDismissed().subscribe({
            next: (step: QRListStepperStageType) => {
                if (
                    step === QRListStepperStageType.GenerateQR ||
                    step === QRListStepperStageType.ReviewQR
                ) {
                    this.companyStore.resetQrDetails();
                    this.companyStore.resetGenerateQRDetails();
                    this.initClientDetailInfo();
                }
            },
        });
    }
}
